'use client';

import * as React from 'react';
import { flushSync } from 'react-dom';

export type ThemeSelection = 'light' | 'dark' | 'system';
export type Resolved = 'light' | 'dark';
export type Direction = 'btt' | 'ttb' | 'ltr' | 'rtl';

export type ChildrenRender =
  | React.ReactNode
  | ((state: {
      resolved: Resolved;
      effective: ThemeSelection;
      toggleTheme: (theme: ThemeSelection, e?: React.MouseEvent) => void;
    }) => React.ReactNode);

function getSystemEffective(): Resolved {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function getClipKeyframes(direction: Direction): [string, string] {
  switch (direction) {
    case 'ltr':
      return ['inset(0 100% 0 0)', 'inset(0 0 0 0)'];
    case 'rtl':
      return ['inset(0 0 0 100%)', 'inset(0 0 0 0)'];
    case 'ttb':
      return ['inset(0 0 100% 0)', 'inset(0 0 0 0)'];
    case 'btt':
      return ['inset(100% 0 0 0)', 'inset(0 0 0 0)'];
    default:
      return ['inset(0 100% 0 0)', 'inset(0 0 0 0)'];
  }
}

export type ThemeTogglerProps = {
  theme: ThemeSelection;
  resolvedTheme: Resolved;
  setTheme: (theme: ThemeSelection) => void;
  direction?: Direction;
  onImmediateChange?: (theme: ThemeSelection) => void;
  children?: ChildrenRender;
};

export function ThemeToggler({
  theme,
  resolvedTheme,
  setTheme,
  onImmediateChange,
  direction = 'ltr',
  children,
  ...props
}: ThemeTogglerProps) {
  const [preview, setPreview] = React.useState<null | {
    effective: ThemeSelection;
    resolved: Resolved;
  }>(null);

  const [current, setCurrent] = React.useState<{
    effective: ThemeSelection;
    resolved: Resolved;
  }>({
    effective: theme,
    resolved: resolvedTheme,
  });

  React.useEffect(() => {
    setCurrent({ effective: theme, resolved: resolvedTheme });
  }, [theme, resolvedTheme]);

  React.useEffect(() => {
    if (
      preview &&
      theme === preview.effective &&
      resolvedTheme === preview.resolved
    ) {
      setPreview(null);
    }
  }, [theme, resolvedTheme, preview]);

  const [fromClip, toClip] = getClipKeyframes(direction);

  const toggleTheme = React.useCallback(
    async (targetTheme: ThemeSelection, e?: React.MouseEvent) => {
      const resolved = targetTheme === 'system' ? getSystemEffective() : targetTheme;

      setCurrent({ effective: targetTheme, resolved });
      onImmediateChange?.(targetTheme);

      if (targetTheme === 'system' && resolved === resolvedTheme) {
        setTheme(targetTheme);
        return;
      }

      const doc = document as any;
      if (!doc.startViewTransition) {
        flushSync(() => {
          setPreview({ effective: targetTheme, resolved });
        });
        setTheme(targetTheme);
        return;
      }

      if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setTheme(targetTheme);
        return;
      }

      try {
        const transition = doc.startViewTransition(() => {
          flushSync(() => {
            setPreview({ effective: targetTheme, resolved });
            document.documentElement.classList.remove('light', 'dark');
            document.documentElement.classList.add(resolved);
            document.documentElement.setAttribute('data-theme', resolved);
            document.documentElement.style.colorScheme = resolved;
          });
        });

        await transition.ready;

        let keyframes: Keyframe[] = [{ clipPath: fromClip }, { clipPath: toClip }];
        if (e && typeof e.clientX === 'number' && typeof e.clientY === 'number' && e.clientX > 0) {
          const x = e.clientX;
          const y = e.clientY;
          const endRadius = Math.hypot(
            Math.max(x, window.innerWidth - x),
            Math.max(y, window.innerHeight - y)
          );
          keyframes = [
            { clipPath: `circle(0px at ${x}px ${y}px)` },
            { clipPath: `circle(${endRadius}px at ${x}px ${y}px)` },
          ];
        }

        const animation = document.documentElement.animate(
          keyframes,
          {
            duration: 400,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            pseudoElement: '::view-transition-new(root)',
          }
        );

        await animation.finished;
      } catch {
        // Fallback gracefully on any transition failure
      } finally {
        setTheme(targetTheme);
      }
    },
    [onImmediateChange, resolvedTheme, fromClip, toClip, setTheme]
  );

  return (
    <React.Fragment {...props}>
      {typeof children === 'function'
        ? children({
            effective: current.effective,
            resolved: current.resolved,
            toggleTheme,
          })
        : children}
      <style>{`::view-transition-old(root), ::view-transition-new(root){animation:none;mix-blend-mode:normal;}::view-transition-new(root){will-change:clip-path;}`}</style>
    </React.Fragment>
  );
}

export default ThemeToggler;
